import { Injectable } from "@angular/core"
import { BehaviorSubject, Observable, map, of } from "rxjs"
import { Counter } from "../models"
import { environment } from "src/environments/environment"
import { CATEGORIES_KEY, KNG_KEY, AVG_MONTHLY_INCOME_KEY, POSITION_SCORES_KEY } from "../models"
import { LOCATIONS_KEY } from "../models/context/context.model"

@Injectable({
    providedIn: "root",
})
export class LoadingService {
    private _counters: Map<string, Counter> = new Map<string, Counter>()

    constructor() {
        this.init()
    }

    private init(): void {
        const categoriesBs = new BehaviorSubject<number>(0)
        const locationsBs = new BehaviorSubject<number>(0)
        const kngBs = new BehaviorSubject<number>(0)
        const avgMonthlyIncomeBs = new BehaviorSubject<number>(0)
        const positionScoresBs = new BehaviorSubject<number>(0)

        const categoriesCounter: Counter = {
            count: categoriesBs,
            dependencies: [environment.endpointRoutes.categories, environment.endpointRoutes.history],
        }
        const kngCounter: Counter = {
            count: kngBs,
            dependencies: [
                environment.endpointRoutes.kng,
                environment.endpointRoutes.staticFiles,
                environment.endpointRoutes.employee,
            ],
        }
        const locationsCounter: Counter = {
            count: locationsBs,
            dependencies: [environment.endpointRoutes.locations, environment.endpointRoutes.history],
        }
        const avgMonthlyIncomeCounter: Counter = {
            count: avgMonthlyIncomeBs,
            dependencies: [environment.endpointRoutes.averageMonthlyIncome],
        }
        const positionScoresCounter: Counter = {
            count: positionScoresBs,
            dependencies: ["positionScores", environment.endpointRoutes.dependencies],
        }

        this._counters.set(CATEGORIES_KEY, categoriesCounter)
        this._counters.set(LOCATIONS_KEY, locationsCounter)
        this._counters.set(KNG_KEY, kngCounter)
        this._counters.set(AVG_MONTHLY_INCOME_KEY, avgMonthlyIncomeCounter)
        this._counters.set(POSITION_SCORES_KEY, positionScoresCounter)
    }

    categoriesLoading(): Observable<boolean> {
        return this._counters
            .get(CATEGORIES_KEY)!
            .count.asObservable()
            .pipe(map(count => count > 0))
    }

    locationsLoading(): Observable<boolean> {
        return this._counters
            .get(LOCATIONS_KEY)!
            .count.asObservable()
            .pipe(map(count => count > 0))
    }

    kngLoading(): Observable<boolean> {
        return this._counters
            .get(KNG_KEY)!
            .count.asObservable()
            .pipe(map(count => count > 0))
    }

    avgMonthlyIncomeLoading(): Observable<boolean> {
        return this._counters
            .get(AVG_MONTHLY_INCOME_KEY)!
            .count.asObservable()
            .pipe(map(count => count > 0))
    }

    positionScoresLoading(): Observable<boolean> {
        return this._counters
            .get(POSITION_SCORES_KEY)!
            .count.asObservable()
            .pipe(map(count => count > 0))
    }

    increment(key: string): void {
        for (const counter of this._counters.values()) {
            if (counter.dependencies.some(d => key.startsWith(d))) {
                counter.count.next(counter.count.value + 1)
            }
        }
    }

    decrement(key: string): void {
        for (const counter of this._counters.values()) {
            if (counter.dependencies.some(d => key.startsWith(d))) {
                counter.count.next(counter.count.value - 1)
            }
        }
    }
}
